import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/apiClient';

const DEBOUNCE_MS = 300;

export default function GlobalSearchBar() {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const inputRef    = useRef(null);
  const dropdownRef = useRef(null);
  const timerRef    = useRef(null);
  const navigate    = useNavigate();

  /* ── Search with debounce ── */
  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const [stockRes, cryptoRes] = await Promise.allSettled([
        apiFetch(`/api/stock/search/${encodeURIComponent(q.trim())}?assetType=Stock`).then(r => r.json()),
        apiFetch(`/api/stock/search/${encodeURIComponent(q.trim())}?assetType=Crypto`).then(r => r.json()),
      ]);
      const stocks = stockRes.status === 'fulfilled' && Array.isArray(stockRes.value) ? stockRes.value : [];
      const crypto = cryptoRes.status === 'fulfilled' && Array.isArray(cryptoRes.value) ? cryptoRes.value : [];
      const merged = [...stocks, ...crypto].slice(0, 10);
      setResults(merged);
      setOpen(merged.length > 0);
      setActiveIdx(-1);
    } catch {
      setResults([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!query.trim()) { setResults([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(() => doSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [query, doSearch]);

  /* ── Close on outside click ── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* ── Navigate to selected item ── */
  const selectItem = useCallback((item) => {
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
    const path = item.assetType === 'Crypto'
      ? `/crypto/${item.symbol}`
      : `/stocks/${item.symbol}`;
    navigate(path);
  }, [navigate]);

  /* ── Keyboard navigation ── */
  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && results[activeIdx]) selectItem(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div style={s.wrapper}>
      <style>{`#gsb-input::placeholder{color:rgba(255,255,255,0.45)}`}</style>
      {/* Input */}
      <div style={s.inputWrap}>
        <span style={s.searchIcon}>
          <SearchIcon loading={loading} />
        </span>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search stocks & crypto…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          id="gsb-input"
          style={s.input}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setOpen(false); inputRef.current?.focus(); }}
            style={s.clearBtn}
            tabIndex={-1}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div ref={dropdownRef} style={s.dropdown}>
          {results.map((item, idx) => (
            <button
              key={item.symbol}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectItem(item); }}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                ...s.resultItem,
                ...(idx === activeIdx ? s.resultItemActive : {}),
              }}
            >
              <span style={s.symbol}>{item.symbol}</span>
              <span style={s.name}>{item.name}</span>
              <span style={{
                ...s.badge,
                ...(item.assetType === 'Crypto' ? s.badgeCrypto : s.badgeStock),
              }}>
                {item.assetType === 'Crypto' ? 'Crypto' : 'Stock'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchIcon({ loading }) {
  if (loading) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </svg>
    );
  }
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

const s = {
  wrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto',
  },
  // placeholder colour is injected via a <style> tag in the render
  inputWrap: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(128,128,128,0.15)',
    border: '1px solid rgba(128,128,128,0.25)',
    borderRadius: '10px',
    padding: '0 10px',
    height: '36px',
    gap: '8px',
  },
  searchIcon: {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.6)',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    minWidth: 0,
  },
  clearBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: '12px',
    flexShrink: 0,
    lineHeight: 1,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    right: 0,
    background: 'var(--surface-strong)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
    zIndex: 300,
    overflow: 'hidden',
    padding: '4px',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    color: 'var(--text-primary)',
  },
  resultItemActive: {
    background: 'var(--surface)',
  },
  symbol: {
    fontWeight: 700,
    fontSize: '14px',
    minWidth: '70px',
    flexShrink: 0,
    color: 'var(--text-primary)',
  },
  name: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  badge: {
    fontSize: '11px',
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: '6px',
    flexShrink: 0,
    letterSpacing: '0.3px',
  },
  badgeStock: {
    background: 'rgba(99,102,241,0.15)',
    color: 'var(--accent)',
  },
  badgeCrypto: {
    background: 'rgba(245,158,11,0.15)',
    color: '#f59e0b',
  },
};
